import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { customerInvoicesApi } from '../lib/api';
import { FileText, CreditCard, AlertCircle, CheckCircle, Clock, XCircle } from 'lucide-react';
import clsx from 'clsx';
import Pagination from '../components/Pagination';
import { readPagination, type PageInfo } from '../lib/pagination';
import InvoiceCfdi from '../components/InvoiceCfdi';
import { useTranslation } from 'react-i18next';

interface Invoice {
  id: string;
  number: string;
  status: string;
  cfdiStatus?: 'NOT_REQUIRED' | 'PENDING' | 'STAMPED' | 'FAILED';
  cfdiUuid?: string | null;
  cfdiSerie?: string | null;
  cfdiFolio?: string | null;
  cfdiXmlUrl?: string | null;
  cfdiPdfUrl?: string | null;
  total: number;
  paidAmount: number;
  currency: string;
  issueDate: string;
  dueDate: string;
  items: Array<{
    id: string;
    description: string;
    quantity: number;
    unitPrice: number;
    total: number;
  }>;
}

const statusConfig: Record<string, { icon: any; color: string; bg: string }> = {
  PAID: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-100' },
  PENDING: { icon: Clock, color: 'text-yellow-600', bg: 'bg-yellow-100' },
  SENT: { icon: Clock, color: 'text-blue-600', bg: 'bg-blue-100' },
  OVERDUE: { icon: AlertCircle, color: 'text-red-600', bg: 'bg-red-100' },
  CANCELLED: { icon: XCircle, color: 'text-gray-600', bg: 'bg-gray-100' },
  REFUNDED: { icon: XCircle, color: 'text-purple-600', bg: 'bg-purple-100' },
  DRAFT: { icon: FileText, color: 'text-gray-600', bg: 'bg-gray-100' },
};

export default function InvoicesPage() {
  const { t } = useTranslation();
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState<PageInfo | null>(null);

  // A different filter is a different list; the page you were on does not
  // survive it.
  useEffect(() => setPage(1), [statusFilter]);

  useEffect(() => {
    loadInvoices();
  }, [statusFilter, page]);

  async function loadInvoices() {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      
      params.page = page;

      const response = await customerInvoicesApi.list(params);
      setInvoices((response.data as any) || []);
      setPageInfo(readPagination(response));
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }

  function formatCurrency(amount: number, currency: string) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
    }).format(amount / 100);
  }

  function formatDate(dateString: string) {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <div className="flex items-center gap-2 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span>{error}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('invoices.title')}</h1>
          <p className="text-gray-600">{t('invoices.subtitle')}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-2">
        {['', 'PENDING', 'PAID', 'OVERDUE'].map((status) => (
          <button
            key={status}
            onClick={() => setStatusFilter(status)}
            className={clsx(
              'px-4 py-2 rounded-lg text-sm font-medium transition-colors',
              statusFilter === status
                ? 'bg-brand-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            )}
          >
            {status || 'All'}
          </button>
        ))}
      </div>

      {/* Invoices Table */}
      {invoices.length === 0 ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <FileText className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-4 text-lg font-medium text-gray-900">{t('invoices.none')}</h3>
          <p className="mt-2 text-gray-500">{t('invoices.noneYet')}</p>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.invoice')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.date')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.dueDate')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.amount')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.status')}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">
                  {t('invoices.actions')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {invoices.map((invoice) => {
                const config = (statusConfig[invoice.status as keyof typeof statusConfig] ?? statusConfig.DRAFT)!;
                const StatusIcon = config.icon;
                const amountDue = invoice.total - invoice.paidAmount;
                const canPay = ['PENDING', 'SENT', 'OVERDUE'].includes(invoice.status) && amountDue > 0;

                return (
                  <tr key={invoice.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <Link
                        to={`/invoices/${invoice.id}`}
                        className="text-brand-600 hover:text-brand-800 font-medium"
                      >
                        {invoice.number}
                      </Link>
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatDate(invoice.issueDate)}
                    </td>
                    <td className="px-6 py-4 text-gray-600">
                      {formatDate(invoice.dueDate)}
                    </td>
                    <td className="px-6 py-4 font-medium">
                      {formatCurrency(invoice.total, invoice.currency)}
                    </td>
                    <td className="px-6 py-4">
                      <span
                        className={clsx(
                          'inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium',
                          config.bg,
                          config.color
                        )}
                      >
                        <StatusIcon className="h-3.5 w-3.5" />
                        {invoice.status}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link
                          to={`/invoices/${invoice.id}`}
                          className="text-gray-600 hover:text-gray-900 text-sm"
                        >
                          {t('invoices.view')}
                        </Link>
                        {canPay && (
                          <Link
                            to={`/invoices/${invoice.id}?pay=true`}
                            className="inline-flex items-center gap-1 px-3 py-1.5 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700"
                          >
                            <CreditCard className="h-4 w-4" />
                            {t('invoices.payNow')}
                          </Link>
                        )}
                        <InvoiceCfdi
                          invoice={invoice}
                          paid={invoice.status === 'PAID'}
                          onRequested={loadInvoices}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {pageInfo && <Pagination {...pageInfo} onChange={setPage} />}
    </div>
  );
}
